import type { TvPuttEvent } from './protocol';
import { startLineBucket } from './startLineBands';
import { formatDistance, formatSpeed , readoutSpeedMps } from './units';

interface MetricsHudProps {
  title: string;
  /** Latest putt event, already freshness-gated by the caller. */
  event: TvPuttEvent | null;
  sessionN: number;
  sessionAvgFt: number | null;
}

/**
 * The metrics surface (Cole, 2026-08-12: "show metrics HUD if that's what
 * the mode calls for"). Modes that have no 3D scene, Start Line practice
 * today, get big readouts in the course's place instead of a context card
 * floating over a stale backdrop.
 *
 * Grades are CATEGORICAL on purpose (PURE/SLIGHT/WIDE/MAJOR mirrors the
 * app's ladder); no signed decimals here — same reasoning as the phone.
 */
export default function MetricsHud({ title, event, sessionN, sessionAvgFt }: MetricsHudProps) {
  const grade = event?.angleDeg != null ? startLineBucket(event.angleDeg) : null;
  const tone = grade === 'PURE' ? 'gold' : grade?.startsWith('SLIGHT') ? 'neutral' : 'danger';
  return (
    <section className="metrics-hud" aria-live="polite">
      <p className="metrics-kicker">{title.toUpperCase()}</p>
      {event ? (
        <>
          <p className={`metrics-grade tone-${tone}`}>{grade ?? 'ROLLED'}</p>
          <div className="metrics-tiles">
            <div>
              <span>LAST ROLL</span>
              <strong>{formatDistance(event.distanceFt)}</strong>
            </div>
            <div>
              <span>BALL SPEED</span>
              <strong>{formatSpeed(readoutSpeedMps(event))}</strong>
            </div>
            <div>
              <span>SESSION</span>
              <strong>
                {sessionN} putt{sessionN === 1 ? '' : 's'}
                {sessionAvgFt != null ? ` · ${formatDistance(sessionAvgFt).toLowerCase()} avg` : ''}
              </strong>
            </div>
          </div>
        </>
      ) : (
        <p className="metrics-waiting">Roll a putt — every putt grades here.</p>
      )}
    </section>
  );
}
